import { ensureLocalizedPath, useLocale } from "@/modules/navigation"
import { cn } from "@/utils/cn"
import Link from "next/link"
import { PropsWithChildren } from "react"

export interface SanityCtaProps {
  label?: string
  type: "external" | "internal"
  externalLink?: string
  internalLink?: {
    category?: {
      slug: string | null
    }
    slug: string | null
  }
  linkTarget?: "_blank" | "_self"
}

interface CtaProps extends PropsWithChildren {
  className?: string
  sanity?: SanityCtaProps
  variant?: "primary" | "ghost" | "blank"
  disabled?: boolean
  type?: "button" | "submit" | "reset"
  onClick?: () => void
}

export function Cta({
  children,
  sanity,
  className,
  variant = "primary",
  disabled = false,
  type,
  onClick,
}: CtaProps) {
  const baseClassName = cn(
    "inline-block cursor-pointer font-family-gtpressura text-xmd uppercase",
    disabled && "pointer-events-none cursor-not-allowed opacity-50",
    variant === "primary" &&
      "rounded-sm border-2 border-primary-500 bg-green-500 px-8 py-3 text-primary-500 transition-all hover:bg-primary-500 hover:text-green-500",
    variant === "ghost" && "text-neutral-light",
    className
  )

  // If type is provided, render as button
  if (type) {
    return (
      <button
        type={type}
        onClick={onClick}
        disabled={disabled}
        className={baseClassName}
      >
        {children || sanity?.label}
      </button>
    )
  }

  // Otherwise render as Link (existing behavior)
  let href =
    sanity?.type === "internal"
      ? sanity?.internalLink?.slug || "/"
      : sanity?.externalLink || "/"
  if (sanity?.internalLink?.category) {
    href = `/${sanity.internalLink.category.slug}/${sanity.internalLink.slug}`
  } else if (sanity?.internalLink?.slug) {
    href = `/${sanity.internalLink.slug}`
  }
  const target = sanity?.linkTarget || "_self"

  const { locale } = useLocale()
  const localeHref = ensureLocalizedPath(href, locale)

  return (
    <Link href={localeHref} target={target} className={baseClassName}>
      {children || sanity?.label}
    </Link>
  )
}
